import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import sr from '@utils/sr';
import { srConfig } from '@config';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';
import { useLanguage, translations } from '@i18n';

const StyledProjectsGrid = styled.ul`
  ${({ theme }) => theme.mixins.resetList};

  a {
    position: relative;
    z-index: 1;
  }
`;

const StyledProject = styled.li`
  position: relative;
  display: grid;
  grid-gap: 10px;
  grid-template-columns: repeat(12, 1fr);
  align-items: center;

  @media (max-width: 768px) {
    ${({ theme }) => theme.mixins.boxShadow};
  }

  &:not(:last-of-type) {
    margin-bottom: 100px;

    @media (max-width: 768px) {
      margin-bottom: 70px;
    }

    @media (max-width: 480px) {
      margin-bottom: 30px;
    }
  }

  &:nth-of-type(odd) {
    .project-content {
      grid-column: 7 / -1;
      text-align: right;

      @media (max-width: 1080px) {
        grid-column: 5 / -1;
      }
      @media (max-width: 768px) {
        grid-column: 1 / -1;
        padding: 40px 40px 30px;
        text-align: left;
      }
      @media (max-width: 480px) {
        padding: 25px 25px 20px;
      }
    }
    .project-tech-list {
      justify-content: flex-end;

      @media (max-width: 768px) {
        justify-content: flex-start;
      }

      li {
        margin: 0 0 5px 20px;

        @media (max-width: 768px) {
          margin: 0 10px 5px 0;
        }
      }
    }
    .project-links {
      justify-content: flex-end;
      margin-left: 0;
      margin-right: -10px;

      @media (max-width: 768px) {
        justify-content: flex-start;
        margin-left: -10px;
        margin-right: 0;
      }
    }
    .project-image {
      grid-column: 1 / 8;

      @media (max-width: 768px) {
        grid-column: 1 / -1;
      }
    }
    .project-image-fan {
      grid-column: 1 / 8;

      @media (max-width: 768px) {
        grid-column: 1 / -1;
      }
    }
  }

  .project-content {
    position: relative;
    grid-column: 1 / 7;
    grid-row: 1 / -1;

    @media (max-width: 1080px) {
      grid-column: 1 / 9;
    }

    @media (max-width: 768px) {
      display: flex;
      flex-direction: column;
      justify-content: center;
      height: 100%;
      grid-column: 1 / -1;
      padding: 40px 40px 30px;
      z-index: 5;
    }

    @media (max-width: 480px) {
      padding: 30px 25px 20px;
    }
  }

  .project-overline {
    margin: 10px 0;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    font-weight: 400;
  }

  .project-title {
    color: var(--lightest-slate);
    font-size: clamp(24px, 5vw, 28px);

    @media (min-width: 768px) {
      margin: 0 0 20px;
    }

    @media (max-width: 768px) {
      color: var(--white);

      a {
        position: static;

        &:before {
          content: '';
          display: block;
          position: absolute;
          z-index: 0;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
        }
      }
    }
  }

  .project-description {
    ${({ theme }) => theme.mixins.boxShadow};
    position: relative;
    z-index: 2;
    padding: 25px;
    border-radius: var(--border-radius);
    background-color: var(--light-navy);
    color: var(--light-slate);
    font-size: var(--fz-lg);

    @media (max-width: 768px) {
      padding: 20px 0;
      background-color: transparent;
      box-shadow: none;

      &:hover {
        box-shadow: none;
      }
    }

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }

    strong {
      color: var(--white);
      font-weight: normal;
    }
  }

  .project-tech-list {
    display: flex;
    flex-wrap: wrap;
    position: relative;
    z-index: 2;
    margin: 25px 0 10px;
    padding: 0;
    list-style: none;

    li {
      margin: 0 20px 5px 0;
      color: var(--light-slate);
      font-family: var(--font-mono);
      font-size: var(--fz-xs);
      white-space: nowrap;
    }

    @media (max-width: 768px) {
      margin: 10px 0;

      li {
        margin: 0 10px 5px 0;
        color: var(--lightest-slate);
      }
    }
  }

  .project-links {
    display: flex;
    align-items: center;
    position: relative;
    margin-top: 10px;
    margin-left: -10px;
    color: var(--lightest-slate);

    a {
      ${({ theme }) => theme.mixins.flexCenter};
      padding: 10px;

      &.external {
        svg {
          width: 22px;
          height: 22px;
          margin-top: -4px;
        }
      }

      svg {
        width: 20px;
        height: 20px;
      }
    }

    .cta {
      ${({ theme }) => theme.mixins.smallButton};
      margin: 10px;
    }
  }

  .project-image {
    ${({ theme }) => theme.mixins.boxShadow};
    grid-column: 6 / -1;
    grid-row: 1 / -1;
    position: relative;
    z-index: 1;

    @media (max-width: 768px) {
      grid-column: 1 / -1;
      height: 100%;
      opacity: 0.25;
    }

    a {
      width: 100%;
      height: 100%;
      /* background-color: var(--green); */
      border-radius: var(--border-radius);
      vertical-align: middle;

      &:hover,
      &:focus {
        background: transparent;
        outline: 0;

        &:before,
        .img {
          background: transparent;
          filter: none;
        }
      }

      /* &:before {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 3;
        transition: var(--transition);
        background-color: var(--navy);
        mix-blend-mode: screen;
      } */
    }

    .img {
      border-radius: var(--border-radius);
      /* mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1) brightness(90%); */

      @media (max-width: 768px) {
        object-fit: cover;
        width: auto;
        height: 100%;
        /* filter: grayscale(100%) contrast(1) brightness(50%); */
      }
    }
  }

  .project-image-fan {
    grid-column: 6 / -1;
    grid-row: 1 / -1;
    position: relative;
    z-index: 1;
    width: 100%;
    height: 0;
    padding-bottom: 60%;

    @media (max-width: 768px) {
      grid-column: 1 / -1;
      height: 100%;
      padding-bottom: 0;
      opacity: 0.25;
    }
  }
`;

const StyledCardFan = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 280px;

  @media (max-width: 768px) {
    min-height: 220px;
  }

  a {
    display: block;
    position: absolute;
    bottom: -8%;
    left: 50%;
    width: 25%;
    transform-origin: bottom center;
    transition: transform 0.35s ease, box-shadow 0.35s ease;
    border-radius: var(--border-radius);
    box-shadow: 0 10px 30px -10px var(--navy-shadow);

    &:hover,
    &:focus {
      outline: 0;
    }

    .img {
      display: block;
      width: 100%;
      height: auto;
      border-radius: var(--border-radius);
    }
  }

  .fan-card-left {
    transform: translateX(-50%) rotate(-15deg);
    z-index: 1;
  }

  .fan-card-center {
    transform: translateX(-50%) translateY(-10px) rotate(0deg);
    z-index: 3;
  }

  .fan-card-right {
    transform: translateX(-50%) rotate(15deg);
    z-index: 2;
  }

  &:hover {
    .fan-card-left {
      transform: translateX(-50%) translateX(-30%) rotate(-22deg);
    }
    .fan-card-right {
      transform: translateX(-50%) translateX(30%) rotate(22deg);
    }
    .fan-card-center {
      transform: translateX(-50%) translateY(-20px) rotate(0deg);
    }
  }
`;

const Featured = () => {
  const data = useStaticQuery(graphql`
    {
      featured: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/featured/" } }
        sort: { fields: [frontmatter___date], order: ASC }
      ) {
        edges {
          node {
            frontmatter {
              title
              cover {
                childImageSharp {
                  gatsbyImageData(width: 700, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                }
              }
              cover2 {
                childImageSharp {
                  gatsbyImageData(width: 700, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                }
              }
              cover3 {
                childImageSharp {
                  gatsbyImageData(width: 700, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                }
              }
              tech
              github
              external
              cta
            }
            html
            parent {
              ... on File {
                relativeDirectory
              }
            }
          }
        }
      }
    }
  `);

  const featuredProjects = data.featured.edges.filter(({ node }) => node);
  const revealTitle = useRef(null);
  const revealProjects = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();
  const { t, lang } = useLanguage();

  // Minimal markdown-to-HTML for translation strings (**bold**, *em*).
  const mdToHtml = src =>
    src.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>').replace(/\*([^*]+)\*/g, '<em>$1</em>');

  const featuredBodyHtml = slug => {
    const parts = [];
    let i = 1;
    while (translations[`featured.${slug}.p${i}`]) {
      parts.push(`<p>${mdToHtml(t(`featured.${slug}.p${i}`))}</p>`);
      i++;
    }
    if (parts.length === 0) {
      return null;
    }
    return parts.join('\n');
  };

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    revealProjects.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  return (
    <section id="projects">
      <h2 className="numbered-heading" ref={revealTitle}>
        {t('featured.heading')}
      </h2>

      <StyledProjectsGrid>
        {featuredProjects &&
          featuredProjects.map(({ node }, i) => {
            const { frontmatter, html } = node;
            const { external, title, tech, github, cover, cover2, cover3, cta } = frontmatter;
            const image = getImage(cover);
            const image2 = getImage(cover2);
            const image3 = getImage(cover3);
            const hasFan = Boolean(image2 && image3);
            const linkHref = external ? external : github ? github : '#';
            const slug = (() => {
              const dir = (node.parent && node.parent.relativeDirectory) || '';
              const parts = dir.split('/').filter(Boolean);
              return parts[parts.length - 1] || '';
            })();
            const localizedTitle = translations[`featured.${slug}.title`]
              ? t(`featured.${slug}.title`)
              : title;
            const localizedHtml = featuredBodyHtml(slug) || html;

            return (
              <StyledProject key={i} ref={el => (revealProjects.current[i] = el)}>
                <div className="project-content">
                  <div>
                    <p className="project-overline">{t('featured.overline')}</p>

                    <h3 className="project-title">
                      <a href={external}>{localizedTitle}</a>
                    </h3>

                    <div
                      key={lang}
                      className="project-description"
                      dangerouslySetInnerHTML={{ __html: localizedHtml }}
                    />

                    {tech.length && (
                      <ul className="project-tech-list">
                        {tech.map((tech, i) => (
                          <li key={i}>{tech}</li>
                        ))}
                      </ul>
                    )}

                    <div className="project-links">
                      {cta && (
                        <a href={cta} aria-label="Course Link" className="cta">
                          {t('featured.learnMore')}
                        </a>
                      )}
                      {github && (
                        <a href={github} aria-label="GitHub Link">
                          <Icon name="GitHub" />
                        </a>
                      )}
                      {external && !cta && (
                        <a href={external} aria-label="External Link" className="external">
                          <Icon name="External" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <div className={hasFan ? 'project-image-fan' : 'project-image'}>
                  {hasFan ? (
                    <StyledCardFan>
                      <a href={linkHref} className="fan-card-left" aria-label={localizedTitle}>
                        <GatsbyImage
                          image={image}
                          alt={`${localizedTitle} screenshot 1`}
                          className="img"
                        />
                      </a>
                      <a href={linkHref} className="fan-card-right" aria-label={localizedTitle}>
                        <GatsbyImage
                          image={image3}
                          alt={`${localizedTitle} screenshot 3`}
                          className="img"
                        />
                      </a>
                      <a href={linkHref} className="fan-card-center" aria-label={localizedTitle}>
                        <GatsbyImage
                          image={image2}
                          alt={`${localizedTitle} screenshot 2`}
                          className="img"
                        />
                      </a>
                    </StyledCardFan>
                  ) : (
                    <a href={linkHref}>
                      <GatsbyImage image={image} alt={localizedTitle} className="img" />
                    </a>
                  )}
                </div>
              </StyledProject>
            );
          })}
      </StyledProjectsGrid>
    </section>
  );
};

export default Featured;
